import React from 'react';
import { Link } from 'react-router-dom';
import { Ship, Anchor, ShoppingCart, Wallet, Camera, ShieldCheck, ArrowRight } from 'lucide-react';

export const Partners = () => {
  const features = [
    { icon: ShoppingCart, color: '#FF6A00', title: 'Викуп на Taobao, 1688, Alibaba', text: 'Купимо будь-який товар за вас — навіть якщо продавець не працює з іноземцями' },
    { icon: Wallet, color: '#22c55e', title: 'Оплата в юанях напряму', text: 'Платимо постачальнику юанями за вигідним курсом без зайвих комісій банків' },
    { icon: Camera, color: '#06b6d4', title: 'Фотозвіт зі складу', text: 'Перевіримо товар на нашому складі в Китаї та надішлемо фото перед відправкою' },
    { icon: ShieldCheck, color: '#a855f7', title: 'Страховка 3%', text: 'Застрахуємо вантаж на повну вартість — компенсація при втраті чи пошкодженні' },
  ];
  const steps = ['Надсилаєте посилання на товар', 'Розраховуємо повну вартість', 'Викуповуємо та перевіряємо', 'Доставляємо в Україну'];
  return (
    <section className="py-16 bg-[#0a0f1c] border-y border-slate-800 relative overflow-hidden" data-testid="vykup-block">
      <div className="absolute inset-0 opacity-[0.15]" style={{backgroundImage: 'radial-gradient(circle, #334155 1px, transparent 1px)', backgroundSize: '28px 28px'}} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/15 border border-orange-500/30 rounded-full mb-4">
            <ShoppingCart className="w-5 h-5 text-orange-400" />
            <span className="text-orange-400 font-semibold">Викуп товарів</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Викуп товарів з Китаю під ключ</h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">Знайшли товар на китайському сайті? Викупимо, перевіримо та доставимо — ви платите в гривні, ми беремо все інше на себе</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {features.map((f, i) => (
            <div key={i} className="group p-5 rounded-2xl bg-[#111827]/70 border border-slate-700/40 hover:border-transparent transition-all duration-300 hover:shadow-[0_0_30px_-8px_var(--glow)]" style={{'--glow': f.color}}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110" style={{background: `${f.color}20`, border: `1px solid ${f.color}50`}}>
                <f.icon className="w-6 h-6" style={{color: f.color}} />
              </div>
              <p className="font-semibold text-white mb-2">{f.title}</p>
              <p className="text-sm text-slate-400 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-0 mb-10">
          {steps.map((s, i) => (
            <React.Fragment key={i}>
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-orange-500/15 border border-orange-500/40 text-orange-400 font-bold text-sm flex items-center justify-center shrink-0">{i + 1}</span>
                <span className="text-slate-300 text-sm font-medium">{s}</span>
              </div>
              {i < steps.length - 1 && <ArrowRight className="w-4 h-4 text-slate-600 mx-4 hidden md:block" />}
            </React.Fragment>
          ))}
        </div>
        <div className="text-center">
          <Link to="/register">
            <button className="h-13 px-8 py-3.5 text-base font-extrabold rounded-full bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-[#1a0a00] shadow-[0_0_25px_rgba(251,146,60,0.55)] hover:shadow-[0_0_45px_rgba(251,146,60,0.9)] hover:scale-[1.03] transition-all" data-testid="vykup-cta-btn">
              Замовити викуп товару
            </button>
          </Link>
          <p className="text-xs text-slate-500 mt-3">Комісія від 5% · Курс юаня фіксується на момент оплати</p>
        </div>
      </div>
    </section>
  );
};

export const ShippingPartners = () => {
  const partners = [
    {
      name: 'Maersk',
      logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e6/Maersk_Group_Logo.svg/320px-Maersk_Group_Logo.svg.png',
      color: '#00243D',
      description: 'Найбільша контейнерна судноплавна компанія світу. Данська компанія з флотом понад 700 суден.',
      founded: '1904'
    },
    {
      name: 'MSC',
      logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/79/Mediterranean_Shipping_Company_logo.svg/320px-Mediterranean_Shipping_Company_logo.svg.png',
      color: '#1E3A5F',
      description: 'Mediterranean Shipping Company - друга за величиною судноплавна лінія у світі. Швейцарсько-італійська компанія.',
      founded: '1970'
    },
    {
      name: 'CMA CGM',
      logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6e/CMA_CGM_logo.svg/320px-CMA_CGM_logo.svg.png',
      color: '#E30613',
      description: 'Французька судноплавна компанія, третя за величиною у світі. Обслуговує 420 портів у 160 країнах.',
      founded: '1978'
    },
    {
      name: 'COSCO Shipping',
      logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c9/COSCO_logo.svg/320px-COSCO_logo.svg.png',
      color: '#0066B3',
      description: 'China Ocean Shipping Company - найбільша судноплавна компанія Китаю. Державне підприємство з флотом 1300+ суден.',
      founded: '1961'
    },
    {
      name: 'Hapag-Lloyd',
      logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f8/Hapag-Lloyd_logo.svg/320px-Hapag-Lloyd_logo.svg.png',
      color: '#FF6600',
      description: 'Німецька судноплавна компанія, одна з найстаріших у світі. Обслуговує 600 портів у 135 країнах.',
      founded: '1847'
    }
  ];

  return (
    <section className="py-20 bg-[#0a0f1c] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/15 border border-blue-500/30 rounded-full mb-4">
            <Anchor className="w-5 h-5 text-blue-400" />
            <span className="text-blue-400 font-semibold">Морські перевезення</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Морські лінії, з якими ми працюємо</h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Найбільші контейнерні перевізники світу
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {partners.map((partner, index) => (
            <div key={partner.name}
              className="group relative bg-[#111827]/80 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:border-transparent transition-all duration-500 overflow-hidden hover:shadow-[0_0_35px_-10px_var(--glow)]"
              style={{'--glow': partner.color}}>
              <div className={`absolute top-0 left-0 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-500`} style={{background: `linear-gradient(to right, transparent, ${partner.color}, transparent)`}} />
              <div className="h-16 flex items-center justify-center mb-4 relative">
                <img src={partner.logo} alt={partner.name} loading="lazy" decoding="async"
                  className="max-h-12 max-w-[180px] object-contain filter brightness-75 group-hover:brightness-100 transition-all duration-500"
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                <div className="hidden items-center justify-center text-2xl font-bold" style={{ color: partner.color }}>{partner.name}</div>
              </div>
              <div className="relative">
                <h3 className="text-xl font-bold text-white mb-3">{partner.name}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{partner.description}</p>
              </div>
              <div className="absolute bottom-0 left-0 h-[2px] w-0 group-hover:w-full transition-all duration-500" style={{ backgroundColor: partner.color }} />
            </div>
          ))}
        </div>

        <div className="mt-16 relative h-24 overflow-hidden">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full h-0.5 bg-gradient-to-r from-transparent via-blue-500/30 to-transparent" />
          </div>
          <div className="absolute top-1/2 -translate-y-1/2" style={{animation: 'shipMove 15s linear infinite'}}>
            <div className="bg-[#111827] rounded-full p-3 shadow-lg border border-blue-500/30">
              <Ship className="w-8 h-8 text-blue-400" />
            </div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: '5+', label: 'Морських ліній' },
            { value: '500+', label: 'Портів доставки' },
            { value: '150+', label: 'Країн охоплення' },
            { value: '10K+', label: 'Контейнерів на рік' }
          ].map((stat, index) => (
            <div key={stat.label} className="text-center p-4">
              <div className="text-3xl md:text-4xl font-bold text-blue-400 mb-1">{stat.value}</div>
              <div className="text-slate-500 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes shipMove { 0% { left: -60px; } 100% { left: calc(100% + 60px); } }
      `}</style>
    </section>
  );
};
